import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config";
import { isMasterAdmin } from "../utils/auth";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";

/**
 * getCsrfToken()
 *
 * Optional CSRF token retrieval helper.
 *
 * Many cookie-auth setups expose a CSRF token via a <meta> tag (or a readable cookie)
 * so the frontend can attach it to requests.
 *
 * IMPORTANT:
 *  - This is NOT the auth token. Auth should be handled by httpOnly cookies (server-set).
 *  - The backend must validate this CSRF token for it to provide protection.
 */
const getCsrfToken = () => {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el?.getAttribute("content") || "";
};

/**
 * useInventorySnapshot(useGhgEndpoint, enabled, mode)
 *
 * Purpose:
 *   Fetches a snapshot payload from either:
 *     - /api/inventory/snapshot
 *     - /api/ghg/snapshot
 *
 * Key features:
 *   - AbortController cancels in-flight request on unmount or dependency change.
 *   - `enabled` allows the caller to disable the fetch entirely.
 *   - `mode === "meta"` uses the same snapshot endpoint with a meta=1 query param.
 *   - Adds show_all query parameter based on client-side user role (UX hint only).
 *
 * SECURITY HARDENING APPLIED (same approach as earlier hooks):
 *   1) Remove Authorization: Bearer ... from localStorage
 *      -> Avoids XSS token theft risk from localStorage.
 *
 *   2) Avoid mixed auth (Bearer token + cookies)
 *      -> Uses cookie-based auth only (credentials: "include").
 *
 *   3) Optional CSRF header support
 *      -> Attaches X-CSRF-Token if available (harmless for GET, useful if server expects it).
 *
 *   4) Avoid surfacing raw backend error messages directly to users
 *      -> Shows user-safe error messages; logs details for developers.
 *
 */
export function useInventorySnapshot(
  useGhgEndpoint = false,
  enabled = true,
  mode = "full"
) {
  // only cache full/snapshot modes, not meta (meta is lightweight dropdown data)
  const isCacheable = mode !== "meta";
  const cacheKey = useGhgEndpoint ? CACHE_KEYS.RECEIVER_GHG : CACHE_KEYS.RECEIVER_INVENTORY;

  // cache context — persists data across tab navigations (matches donor pattern)
  const { getCachedData, setCachedData } = useDataCache();
  const getCacheRef = useRef(getCachedData);
  const setCacheRef = useRef(setCachedData);
  useEffect(() => { getCacheRef.current = getCachedData; }, [getCachedData]);
  useEffect(() => { setCacheRef.current = setCachedData; }, [setCachedData]);

  // -------------------------
  // State: data + network status
  // lazy initializers pull from cache synchronously on first render so
  // tab-click navigations don't flash a spinner before the effect hydrates.
  // -------------------------
  const [data, setData] = useState(() => {
    if (!enabled || !isCacheable) return null;
    const cached = getCachedData(cacheKey);
    return cached?.data ?? null;
  });

  const [loading, setLoading] = useState(() => {
    if (!enabled) return false;
    if (!isCacheable) return true;
    const cached = getCachedData(cacheKey);
    return !cached?.data;
  });

  // User-visible error message.
  const [error, setError] = useState("");

  // timestamp of last successful fetch — consumed by LastUpdatedBadge
  const [lastUpdated, setLastUpdated] = useState(() => {
    if (!enabled || !isCacheable) return null;
    const cached = getCachedData(cacheKey);
    return cached?.timestamp ?? null;
  });

  // bumping this triggers the effect to re-fetch
  const [refreshKey, setRefreshKey] = useState(0);

  const performRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    // If disabled, do not fetch; also ensure loading is false.
    if (!enabled) {
      setLoading(false);
      return;
    }

    /**
     * AbortController cancels this request if:
     *  - component unmounts
     *  - dependencies change and effect re-runs
     */
    const controller = new AbortController();

    /**
     * showAll is computed client-side for UX, but must be validated server-side.
     * A malicious caller can still request show_all=true; the server must enforce.
     */
    const showAll = isMasterAdmin();

    async function load() {
      // on initial mount with cache, skip the fetch entirely — match the
      // donor pattern (cache-first, no background refetch). fresh data only on
      // manual refresh (performRefresh bumps refreshKey).
      const isManualRefresh = refreshKey > 0;
      if (!isManualRefresh && isCacheable) {
        const cached = getCacheRef.current?.(cacheKey);
        if (cached?.data) {
          setData(cached.data);
          setLastUpdated(cached.timestamp || null);
          setLoading(false);
          return;
        }
      }

      try {
        setLoading(true);
        setError("");

        // Choose which snapshot endpoint to call.
        const endpoint = useGhgEndpoint
          ? "/api/ghg/snapshot"
          : "/api/inventory/snapshot";

        /**
         * Build query parameters safely with URLSearchParams.
         * - show_all: UX hint; server must enforce.
         * - meta=1 when mode === "meta" to reuse snapshot endpoint for metadata-only.
         */
        const params = new URLSearchParams();
        params.set("show_all", String(showAll));

        // IMPORTANT: avoids 404 /api/ghg/meta by reusing snapshot endpoint
        if (mode === "meta") params.set("meta", "1");

        // Final URL to fetch.
        const url = `${config.apiUrl}${endpoint}?${params.toString()}`;

        /**
         * Headers:
         * - Accept JSON responses
         * - Optional CSRF header (if your backend expects it)
         *
         * SECURITY NOTE:
         * - We intentionally do NOT attach Authorization: Bearer from localStorage.
         * - Authentication should be performed via httpOnly cookies.
         */
        const headers = {
          Accept: "application/json",
        };

        const csrf = getCsrfToken();
        if (csrf) headers["X-CSRF-Token"] = csrf;

        /**
         * Perform request:
         * - credentials: "include" ensures auth cookies are sent.
         * - signal ties request lifecycle to AbortController.
         */
        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers,
          signal: controller.signal,
        });

        /**
         * Parse response.
         * We read as text first, then attempt JSON parse. This preserves the original logic:
         * - If server sends non-JSON or empty response, JSON stays null.
         */
        const text = await res.text();
        let json = null;
        try {
          json = text ? JSON.parse(text) : null;
        } catch {
          json = null;
        }

        /**
         * Handle non-OK responses:
         * - Log details for developers
         * - Show user-safe message (avoid leaking internal backend error details)
         */
        if (!res.ok) {
          console.error("Snapshot request failed:", {
            url,
            status: res.status,
            statusText: res.statusText,
            body: json ?? text,
          });

          if (res.status === 401) {
            throw new Error("Session expired. Please log in again.");
          }
          if (res.status >= 500) {
            throw new Error("Server error. Please try again later.");
          }

          // Generic client error
          throw new Error("Failed to load snapshot. Please try again.");
        }

        // Success: store parsed JSON (could be null if server returned empty body).
        setData(json);
        const ts = Date.now();
        setLastUpdated(ts);

        // persist to cache for instant revisits
        if (isCacheable && json) {
          try { setCacheRef.current?.(cacheKey, json, ts); } catch { /* ignore */ }
        }
      } catch (e) {
        // AbortError occurs during cleanup; do not surface to user.
        if (e?.name === "AbortError") return;

        // For unexpected errors, log details and show a user-safe message.
        console.error("Snapshot request error:", e);
        setError(e?.message || "Request failed");
      } finally {
        setLoading(false);
      }
    }

    // Kick off the fetch.
    load();

    // Cleanup: abort request when effect is cleaned up.
    return () => controller.abort();
  }, [useGhgEndpoint, enabled, mode, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, lastUpdated, performRefresh };
}